import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Shield, Zap, Lock, Globe, Building2,
  Cpu, Brain, CheckCircle, ArrowRight, Database,
  Cloud, Network, Users, HeartPulse, Clock,
  MapPin, Flag, Sparkles, Bot
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';

const DedicatedServer = () => {
  usePageTitle('Serveur Dédié - Infrastructure Souveraine');
  const navigate = useNavigate();

  const advantages = [
    {
      icon: Shield,
      title: 'Isolation Totale',
      description: 'Infrastructure dédiée et isolée pour une sécurité maximale. Aucun partage de ressources avec d\'autres clients.'
    },
    {
      icon: Zap,
      title: 'Performance Garantie',
      description: 'Ressources CPU, RAM et stockage dédiées. Scans instantanés sans file d\'attente ni limitation.'
    },
    {
      icon: Lock,
      title: 'Souveraineté des Données',
      description: 'Hébergement 100% français, sans contraintes des solutions étrangères (Cloud Act, FISA).'
    },
    {
      icon: HeartPulse,
      title: 'Supervision 24/7',
      description: 'Monitoring continu, alertes en temps réel et intervention proactive de nos équipes.'
    },
    {
      icon: Users,
      title: 'Support Dédié',
      description: 'Équipe technique dédiée, SLA garanti et accompagnement personnalisé.'
    },
    {
      icon: Clock,
      title: 'Disponibilité 99.9%',
      description: 'Infrastructure redondante avec haute disponibilité et plan de reprise d\'activité.'
    }
  ];

  const hostingOptions = [
    {
      icon: Building2,
      title: 'Centre de données France - Reims',
      location: 'Reims, France',
      features: [
        'Certification Tier III',
        'Alimentation électrique redondante',
        'Refroidissement haute efficacité',
        'Sécurité physique 24/7',
        'Connexion fibre 10 Gbps',
        'Conformité RGPD native'
      ],
      highlight: true
    },
    {
      icon: Network,
      title: 'Sur site',
      location: 'Votre infrastructure',
      features: [
        'Installation dans votre réseau',
        'Contrôle total des données',
        'Intégration SIEM/SOC',
        'Accès réseau interne direct',
        'Personnalisation complète',
        'Formation de vos équipes'
      ],
      highlight: false
    }
  ];

  const aiModels = [
    {
      name: 'SYLink IA 8B',
      params: '8 milliards',
      description: 'Modèle léger et rapide pour analyses standards',
      specs: 'GPU: 8GB VRAM minimum',
      useCase: 'Analyses rapides, rapports automatisés',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'SYLink IA 30B',
      params: '30 milliards',
      description: 'Modèle équilibré pour analyses approfondies',
      specs: 'GPU: 24GB VRAM minimum',
      useCase: 'Analyses détaillées, corrélations avancées',
      color: 'from-purple-500 to-pink-500',
      recommended: true
    },
    {
      name: 'SYLink IA 80B',
      params: '80 milliards',
      description: 'Modèle expert pour analyses complexes',
      specs: 'GPU: 80GB VRAM minimum',
      useCase: 'Analyses expertes, recherche de vulnérabilités',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-brand-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/10 via-transparent to-purple-500/10" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-brand-green/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/10 border border-brand-green/30 rounded-full text-brand-green text-sm mb-6">
              <Flag className="w-4 h-4" />
              Solution Souveraine 100% Française
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Infrastructure <span className="text-brand-green">Dédiée</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Une solution de pentest isolée, managée et supervisée pour votre entreprise.
              Hébergée en France, sans contraintes des solutions étrangères.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {[
              { value: '100%', label: 'Français', icon: Flag },
              { value: '0', label: 'File d\'attente', icon: Zap },
              { value: '24/7', label: 'Supervision', icon: HeartPulse },
              { value: '99.9%', label: 'Disponibilité', icon: CheckCircle }
            ].map((stat, idx) => (
              <div key={idx} className="bg-brand-darker/50 backdrop-blur border border-gray-800 rounded-xl p-6 text-center">
                <stat.icon className="w-8 h-8 text-brand-green mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Advantages Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-4">
          Pourquoi choisir une infrastructure dédiée ?
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
          Bénéficiez d'une solution sur-mesure adaptée à vos exigences de sécurité et de performance
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {advantages.map((advantage, idx) => (
            <div
              key={idx}
              className="bg-brand-darker border border-gray-800 rounded-xl p-6 hover:border-brand-green/50 transition-all group"
            >
              <div className="w-12 h-12 bg-brand-green/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-green/20 transition-colors">
                <advantage.icon className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{advantage.title}</h3>
              <p className="text-gray-400 text-sm break-words">{advantage.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Hosting Options */}
      <div className="bg-brand-darker/50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Options d'hébergement
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            Choisissez l'hébergement adapté à vos besoins : dans notre datacenter souverain ou directement dans votre infrastructure
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {hostingOptions.map((option, idx) => (
              <div
                key={idx}
                className={`relative bg-brand-darker border rounded-2xl p-8 ${
                  option.highlight
                    ? 'border-brand-green shadow-lg shadow-brand-green/10'
                    : 'border-gray-800'
                }`}
              >
                {option.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-brand-green text-black text-sm font-medium rounded-full">
                    Recommandé
                  </div>
                )}

                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    option.highlight ? 'bg-brand-green/20' : 'bg-gray-800'
                  }`}>
                    <option.icon className={`w-7 h-7 ${option.highlight ? 'text-brand-green' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{option.title}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <MapPin className="w-3 h-3" />
                      {option.location}
                    </div>
                  </div>
                </div>

                <ul className="space-y-3">
                  {option.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle className={`w-5 h-5 ${option.highlight ? 'text-brand-green' : 'text-gray-500'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm mb-6">
            <Brain className="w-4 h-4" />
            Intelligence Artificielle Locale
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            SYLink IA - Modèles spécialisés Cybersécurité
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Déployez notre IA spécialisée en cybersécurité directement sur votre infrastructure.
            Analyse locale, sans envoi de données vers l'extérieur.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {aiModels.map((model, idx) => (
            <div
              key={idx}
              className={`relative bg-brand-darker border rounded-xl overflow-hidden ${
                model.recommended ? 'border-purple-500' : 'border-gray-800'
              }`}
            >
              {model.recommended && (
                <div className="absolute top-0 right-0 px-3 py-1 bg-purple-500 text-white text-xs font-medium">
                  Recommandé
                </div>
              )}

              <div className={`h-2 bg-gradient-to-r ${model.color}`} />

              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${model.color} flex items-center justify-center`}>
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{model.name}</h3>
                    <p className="text-xs text-gray-400">{model.params} de paramètres</p>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-4 break-words">{model.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Cpu className="w-4 h-4 text-gray-500" />
                    {model.specs}
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Sparkles className="w-4 h-4 text-gray-500" />
                    {model.useCase}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-brand-darker border border-gray-800 rounded-xl p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://ollama.com/public/ollama.png"
              alt="Ollama"
              className="w-12 h-12 rounded-lg"
              onError={(e) => { e.target.style.display = 'none' }}
            />
            <div>
              <div className="text-white font-medium">Propulsé par Ollama</div>
              <div className="text-sm text-gray-400">Exécution locale de modèles LLM</div>
            </div>
          </div>
          <a
            href="https://ollama.com/sylink/sylink:32b"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-800 text-gray-300 font-semibold rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            Voir sur Ollama
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-brand-green/10 via-brand-darker to-purple-500/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Prêt à passer à l'infrastructure dédiée ?
          </h2>
          <p className="text-gray-400 mb-8">
            Contactez notre équipe commerciale pour une étude personnalisée de vos besoins
            et un devis sur-mesure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-8 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-colors flex items-center justify-center gap-2"
            >
              Demander un devis
              <ArrowRight className="w-5 h-5" />
            </a>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3 bg-brand-darker text-white font-semibold rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
            >
              Voir tous les plans
            </button>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Technologie Française</h3>
            <p className="text-gray-400 text-sm break-words">
              Développée en France, sans dépendance aux technologies étrangères
            </p>
          </div>
          <div>
            <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Conformité RGPD</h3>
            <p className="text-gray-400 text-sm break-words">
              Hébergement souverain conforme aux réglementations européennes
            </p>
          </div>
          <div>
            <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Database className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Données Protégées</h3>
            <p className="text-gray-400 text-sm break-words">
              Vos données restent en France, protégées du Cloud Act et FISA
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DedicatedServer;
